import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { PercepthorCanvas } from '../../models/PercepthorCanvas'
import { PercepthorImage } from '../../models/PercepthorImage'

import { loadImageFromUrl } from '../../utils/image'
import { PercepthorArticle } from '../../models/PercepthorArticle';
import { Coordinate } from '../../models/Coordinate';

//Utils
import { COLOR_TRANSPARENCY } from '../../utils/fabricJsCustom'

const MOBILE_BREAKPOINT = 992;
const TAP_MOVE_PX = 10;
const TAP_MAX_MS = 500;
const TAP_TOOLTIP_MS = 2200;

const Canvas = (props) => {
	const { id, urlPhoto, height, canvas, setCanvas, tagsList, predictions } = props;

	const [tapTooltip, setTapTooltip] = useState(null); // { x, y, label, color } | null
	const tooltipTimerRef = useRef(null);
	// `canvas.percepthorImage` is a Fabric mutation, invisible to React —
	// we mirror its readiness in state so the tagsList effect below can
	// re-run once the image finishes loading (otherwise a tagsList that
	// arrives before the image never re-triggers the article-creation loop).
	const [imageReady, setImageReady] = useState(false);

	useEffect(() => {
		async function initCanvas() {
			const localCanvas = new PercepthorCanvas('canvas' + id)
			localCanvas.hoverCursor = 'default';
			localCanvas.cursorMode = 'inspect'
			localCanvas.stopContextMenu = true;
			localCanvas.fireRightClick = true;
			localCanvas.allowTouchScrolling = true;

			// On mobile we need page scrolling to work even when a finger is
			// over a bounding box. `allowTouchScrolling: true` alone is not
			// enough — Fabric still calls preventDefault on touchstart when it
			// finds an object under the pointer. Disabling target find removes
			// that branch entirely. Tap-to-show-label is then reimplemented at
			// the DOM level via the touch listeners on the wrapper below.
			if (window.innerWidth <= MOBILE_BREAKPOINT) {
				localCanvas.skipTargetFind = true;
				localCanvas.selection = false;
			}

			// Force vertical pan to the page so single-finger touch scrolls
			// instead of being captured for fabric selection on mobile.
			if (localCanvas.upperCanvasEl) {
				localCanvas.upperCanvasEl.style.touchAction = 'pan-y';
			}

			// Match canvas dimensions to the wrapper before loading the image
			// so setFitZoom calculates with the real container width.
			const wrapper = document.getElementById('divCanvas' + id);
			if (wrapper && wrapper.offsetWidth > 0 && wrapper.offsetHeight > 0) {
				localCanvas.setDimensions({
					width: wrapper.offsetWidth,
					height: wrapper.offsetHeight,
				});
			}

			setCanvas(localCanvas);

			let imageObj = await loadImageFromUrl(urlPhoto)
			if (imageObj !== null){
				let newPercepthorImage = new PercepthorImage(imageObj)
				newPercepthorImage.set({
					image: imageObj,
					width: imageObj.width,
					height: imageObj.height,
				})

				localCanvas.setPercepthorImage(newPercepthorImage)
				localCanvas.requestRenderAll();
				localCanvas.setFitZoom()

				// Signal image readiness so the [tagsList, imageReady] effect
				// below can pick up and create the articles. The inline
				// creation loop that used to live here ran with the mount-time
				// closure of tagsList/predictions (empty on first paint) and
				// was dead work anyway.
				setImageReady(true);
			}
		}

		initCanvas()
	}, []);

	useEffect(() => { //On change tagsList
		// `canvas` is set as soon as the Fabric instance is created (before the
		// async image load), so we must also gate on `percepthorImage` being
		// ready — otherwise reading `canvas.percepthorImage.width` below blows
		// up with `Cannot read properties of null`. `imageReady` is the state
		// mirror of that Fabric mutation so this effect re-runs when the image
		// finishes loading (previously the effect only fired on tagsList
		// changes; if the image loaded AFTER tagsList arrived, the articles
		// were never created and the canvas rendered empty).
		if (canvas && canvas.percepthorImage && tagsList && predictions){
			canvas.removePercepthorArticles();
			//Creando los percpethorArtuicles
			predictions.forEach(element => {
				tagsList.forEach(tag => {
					if (tag.className === element.clase) {
						let c = new Coordinate(0, 0, 0, 0, element.coords['0'], element.coords['1'], element.coords['2'], element.coords['3'], element.score)
						let [left, top, width, height] = c.getParamsForFabricPerceptorTag(canvas.percepthorImage.width, canvas.percepthorImage.height)
						let rgbAux = tag.arrayRGBColor
						// Doors ship hidden by default — TagList's "Doors" toggle
						// enables them. Setting visibility here (at construction)
						// closes a race with TagList's [canvas, tagsList] effect
						// that hides doors: after image navigation the article
						// creation ran AFTER TagList's effect, so the doors
						// were never hidden on the next photo.
						const isDoor = /puerta/i.test(tag.className);
						let article = new PercepthorArticle({
							//Attributes of Fabric.Rect
							left: left,
							top: top,
							originX: 'left',
							originY: 'top',
							width: width,
							height: height,
							objectCaching: false,
							strokeWidth: 1, //este tambien cuenta en el tamanio del objeto, en with y heigth
							strokeUniform: true,
							fill: `rgba(${rgbAux[0]}, ${rgbAux[1]}, ${rgbAux[2]}, ${COLOR_TRANSPARENCY})`,
							stroke: `rgba(${rgbAux[0]}, ${rgbAux[1]}, ${rgbAux[2]}, 1)`,
							visible: !isDoor,
							//Extended attributes for PercepthorArticle
							id: 0,
							label: '',
							result: null,
							tag: tag,
						});
						article.set({
							lockMovementX: true,
							lockMovementY: true,
							lockScalingX: true,
							lockScalingY: true
						});
						canvas.add(article);
					}
				});
			});
		}
	}, [tagsList, imageReady]);

	//https://stackblitz.com/edit/react-w2v97x?file=index.js
	function useCanvasSize() { //US hoook
		const [size, setSize] = useState([0, 0]);
		useLayoutEffect(() => {
			function updateSize() {
				let newWidth = document.getElementById("divCanvas" + id).offsetWidth
				let newHeight = document.getElementById("divCanvas" + id).offsetHeight
				setSize([newWidth, newHeight]);
			}
			window.addEventListener("resize", updateSize);
			updateSize();
			return () => window.removeEventListener("resize", updateSize);
		}, []);
		if (canvas) {
			canvas.setDimensions({
				width: size[0],
				height: size[1]
			});
		}
		return size;
	}

	const [widthCanvas, heightCanvas] = useCanvasSize(); //It works as EventListener for rezizing canvas

	// Mobile tap-to-show-label. Skipped on desktop (the existing Fabric
	// inspect-mode mousemove popup handles hover there). On touch devices we
	// listen at the wrapper level: a tap = touchstart + touchend with little
	// movement and a short hold; anything else (drag) is left to the browser
	// so the page scrolls. On a real tap we ask Fabric what's under the
	// pointer and pop a small tooltip with the tag name.
	useEffect(() => {
		if (!canvas) return undefined;
		if (window.innerWidth > MOBILE_BREAKPOINT) return undefined;

		const wrapper = document.getElementById('divCanvas' + id);
		if (!wrapper) return undefined;

		let startX = 0, startY = 0, startT = 0, moved = false;

		const onStart = (e) => {
			const t = e.touches[0];
			startX = t.clientX;
			startY = t.clientY;
			startT = Date.now();
			moved = false;
		};
		const onMove = (e) => {
			if (moved) return;
			const t = e.touches[0];
			if (Math.abs(t.clientX - startX) > TAP_MOVE_PX || Math.abs(t.clientY - startY) > TAP_MOVE_PX) {
				moved = true;
			}
		};
		const onEnd = (e) => {
			if (moved) return;
			if (Date.now() - startT > TAP_MAX_MS) return;
			const t = e.changedTouches[0];
			// `findTarget` works even when skipTargetFind=true (it's only the
			// auto-search-on-move that flag disables).
			const target = canvas.findTarget({ clientX: t.clientX, clientY: t.clientY }, true);
			if (!target || target.type !== 'percepthorArticle' || !target.tag) {
				setTapTooltip(null);
				return;
			}
			const [r, g, b] = target.tag.arrayRGBColor;
			const wrapperRect = wrapper.getBoundingClientRect();
			setTapTooltip({
				x: t.clientX - wrapperRect.left,
				y: t.clientY - wrapperRect.top,
				label: target.tag.className,
				color: `rgb(${r}, ${g}, ${b})`,
			});
			clearTimeout(tooltipTimerRef.current);
			tooltipTimerRef.current = setTimeout(() => setTapTooltip(null), TAP_TOOLTIP_MS);
		};

		wrapper.addEventListener('touchstart', onStart, { passive: true });
		wrapper.addEventListener('touchmove', onMove, { passive: true });
		wrapper.addEventListener('touchend', onEnd, { passive: true });
		return () => {
			wrapper.removeEventListener('touchstart', onStart);
			wrapper.removeEventListener('touchmove', onMove);
			wrapper.removeEventListener('touchend', onEnd);
			clearTimeout(tooltipTimerRef.current);
		};
	}, [canvas, id]);

	const canvasLightStyleCSS = {
		height: height,
		backgroundColor:'#f7f7f7'
	}

	const canvasDarkStyleCSS = {
		height: height,
		backgroundColor:'#1d1f20'
	}

	return (
		<div
			id={'divCanvas' + id}
			style={{
				...(localStorage.getItem('theme') === 'dark' ? canvasDarkStyleCSS : canvasLightStyleCSS),
				position: 'relative',
			}}
		>
			<canvas id={"canvas" + id} onContextMenu={(e) => e.preventDefault()}></canvas>
			{tapTooltip && (
				<div
					style={{
						position: 'absolute',
						left: tapTooltip.x,
						top: tapTooltip.y,
						transform: 'translate(-50%, calc(-100% - 10px))',
						maxWidth: '70vw',
						pointerEvents: 'none',
						background: 'rgba(15, 23, 42, 0.92)',
						color: '#fff',
						padding: '6px 10px',
						borderRadius: 8,
						fontSize: 12,
						fontWeight: 600,
						whiteSpace: 'nowrap',
						display: 'inline-flex',
						alignItems: 'center',
						gap: 8,
						boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
						zIndex: 5,
					}}
				>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							background: tapTooltip.color,
							flex: '0 0 auto',
						}}
					/>
					<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tapTooltip.label}</span>
				</div>
			)}
			<p hidden> {widthCanvas}x{heightCanvas}</p>
		</div>
	);
}

export default Canvas;
